import { ClearOutlined, UndoOutlined } from "@ant-design/icons";
import {
	area as turfArea,
	length as turfLength,
	lineString as turfLineString,
	polygon as turfPolygon,
} from "@turf/turf";
import { Button, Space, Tag, Typography } from "antd";
import L from "leaflet";
import { useEffect, useMemo } from "react";
import {
	MapContainer,
	Marker,
	Polygon,
	Polyline,
	TileLayer,
	useMap,
	useMapEvents,
} from "react-leaflet";
import type { GeoJSONPolygon, TerritoryCoordinates } from "../services/territory.service";

interface TerritoryGeometryDrawerProps {
	value?: TerritoryCoordinates | null;
	onChange?: (
		value: TerritoryCoordinates | undefined,
		metrics: { area: number; perimeter: number }
	) => void;
	height?: number;
}

// Convert GeoJSON [lng, lat] to Leaflet [lat, lng]
const toLatLng = ([lng, lat]: [number, number]): [number, number] => [lat, lng];
const toLngLat = (latlng: L.LatLng): [number, number] => [
	Math.round(latlng.lng * 1000000) / 1000000,
	Math.round(latlng.lat * 1000000) / 1000000,
];

// Custom small circle icon for vertices
const vertexIcon = L.divIcon({
	className: "custom-vertex-icon",
	html: `<div style="
		width: 12px;
		height: 12px;
		background: #1677ff;
		border: 2px solid #ffffff;
		border-radius: 50%;
		box-shadow: 0 0 4px rgba(0,0,0,0.4);
		cursor: grab;
	"></div>`,
	iconSize: [12, 12],
	iconAnchor: [6, 6],
});

function InvalidateSize() {
	const map = useMap();
	useEffect(() => {
		const timers = [
			setTimeout(() => map.invalidateSize(), 50),
			setTimeout(() => map.invalidateSize(), 300),
		];
		return () => {
			for (const t of timers) {
				clearTimeout(t);
			}
		};
	}, [map]);
	return null;
}

function FitToBounds({ positions }: { positions: [number, number][] }) {
	const map = useMap();
	useEffect(() => {
		if (positions.length === 1) {
			map.setView(positions[0], 15);
		} else if (positions.length > 1) {
			map.fitBounds(L.latLngBounds(positions), { padding: [30, 30], maxZoom: 16 });
		}
	}, [map, positions]);
	return null;
}

function MapClickHandler({ onAddPoint }: { onAddPoint: (point: [number, number]) => void }) {
	useMapEvents({
		click(e) {
			onAddPoint(toLngLat(e.latlng));
		},
	});
	return null;
}

export function TerritoryGeometryDrawer({
	value,
	onChange,
	height = 360,
}: TerritoryGeometryDrawerProps) {
	// Points stored as [lng, lat]
	const points: [number, number][] = useMemo(() => {
		if (!value) {
			return [];
		}
		let ring: [number, number][] | undefined;
		if (value.type === "MultiPolygon") {
			ring = value.coordinates?.[0]?.[0];
		} else {
			ring = value.coordinates?.[0];
		}
		if (!ring) {
			return [];
		}
		// If last point equals first point, strip the closed duplicate for editing
		if (
			ring.length > 1 &&
			ring[0][0] === ring[ring.length - 1][0] &&
			ring[0][1] === ring[ring.length - 1][1]
		) {
			return ring.slice(0, -1);
		}
		return ring;
	}, [value]);

	const leafletPositions: [number, number][] = useMemo(() => points.map(toLatLng), [points]);

	const metrics = useMemo(() => {
		if (points.length < 3) {
			return { area: 0, perimeter: 0 };
		}
		try {
			const closed = [...points, points[0]];
			const calculatedArea = Math.round(turfArea(turfPolygon([closed])) * 100) / 100;
			const calculatedPerimeter =
				Math.round(turfLength(turfLineString(closed), { units: "meters" }) * 100) / 100;
			return { area: calculatedArea, perimeter: calculatedPerimeter };
		} catch {
			return { area: 0, perimeter: 0 };
		}
	}, [points]);

	const emitUpdate = (newPoints: [number, number][]) => {
		if (newPoints.length === 0) {
			onChange?.(undefined, { area: 0, perimeter: 0 });
			return;
		}

		let newMetrics = { area: 0, perimeter: 0 };
		if (newPoints.length >= 3) {
			try {
				const closed = [...newPoints, newPoints[0]];
				const a = Math.round(turfArea(turfPolygon([closed])) * 100) / 100;
				const p = Math.round(turfLength(turfLineString(closed), { units: "meters" }) * 100) / 100;
				newMetrics = { area: a, perimeter: p };
			} catch {
				// fallback 0
			}
		}

		const polygon: GeoJSONPolygon = {
			type: "Polygon",
			coordinates: [newPoints.length >= 3 ? [...newPoints, newPoints[0]] : newPoints],
		};

		onChange?.(polygon, newMetrics);
	};

	const handleAddPoint = (point: [number, number]) => {
		emitUpdate([...points, point]);
	};

	const handleUndo = () => {
		if (points.length > 0) {
			emitUpdate(points.slice(0, -1));
		}
	};

	const handleClear = () => {
		emitUpdate([]);
	};

	const handleMarkerDrag = (index: number, newLatLng: L.LatLng) => {
		const updated = [...points];
		updated[index] = toLngLat(newLatLng);
		emitUpdate(updated);
	};

	const areaHa = (metrics.area / 10000).toFixed(2);
	const perimeterKm = (metrics.perimeter / 1000).toFixed(2);

	return (
		<div className="space-y-2">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<Space wrap size="small">
					<Button
						size="small"
						icon={<UndoOutlined />}
						onClick={handleUndo}
						disabled={points.length === 0}
					>
						Oxirgi nuqtani bekor qilish
					</Button>
					<Button
						size="small"
						danger
						icon={<ClearOutlined />}
						onClick={handleClear}
						disabled={points.length === 0}
					>
						Tozalash
					</Button>
					<Typography.Text type="secondary" className="text-xs">
						{points.length < 3
							? "Xaritada kamida 3 ta nuqta belgilang (hudud poligonini hosil qilish uchun)"
							: "Nuqtalarni sudrab joyini o'zgartirish yoki yangi nuqta qo'shish mumkin"}
					</Typography.Text>
				</Space>

				<div className="flex items-center gap-2">
					<Tag color={points.length >= 3 ? "blue" : "default"}>
						Nuqtalar: <strong>{points.length}</strong>
					</Tag>
					{points.length >= 3 && (
						<>
							<Tag color="cyan">
								Yuzasi: <strong>{metrics.area.toLocaleString("uz-UZ")} m²</strong> ({areaHa} ga)
							</Tag>
							<Tag color="purple">
								Perimetr: <strong>{metrics.perimeter.toLocaleString("uz-UZ")} m</strong> (
								{perimeterKm} km)
							</Tag>
						</>
					)}
				</div>
			</div>

			<div
				className="w-full overflow-hidden rounded-lg border border-neutral-300 relative"
				style={{ height }}
			>
				<MapContainer center={[41.56, 60.62]} zoom={12} className="h-full w-full" scrollWheelZoom>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>
					<InvalidateSize />
					<MapClickHandler onAddPoint={handleAddPoint} />
					{leafletPositions.length > 0 && <FitToBounds positions={leafletPositions} />}

					{leafletPositions.length >= 3 ? (
						<Polygon
							positions={leafletPositions}
							pathOptions={{
								color: "#1677ff",
								fillColor: "#3b82f6",
								fillOpacity: 0.35,
								weight: 2.5,
							}}
						/>
					) : leafletPositions.length >= 2 ? (
						<Polyline
							positions={leafletPositions}
							pathOptions={{ color: "#1677ff", weight: 2.5, dashArray: "4, 6" }}
						/>
					) : null}

					{leafletPositions.map((pos, idx) => (
						<Marker
							key={`vertex-${pos[0]}-${pos[1]}-${idx}`}
							position={pos}
							icon={vertexIcon}
							draggable
							eventHandlers={{
								dragend: (e) => {
									const marker = e.target as L.Marker;
									handleMarkerDrag(idx, marker.getLatLng());
								},
							}}
						/>
					))}
				</MapContainer>
			</div>
		</div>
	);
}
